import { useState, useEffect, useId, useRef } from 'react';
import { X, ChevronRight, User, Settings, CreditCard, Lock, Trash2, HelpCircle, Gift, Apple, LogOut, Users, Download, FileText, Table2, AlertTriangle, Check, Sparkles } from 'lucide-react';
import { EditProfileDrawer } from './EditProfileDrawer';
import { PreferencesDrawer } from './PreferencesDrawer';
import { ChangePasswordDrawer } from './ChangePasswordDrawer';
import { DeleteAccountDrawer } from './DeleteAccountDrawer';
import BottomNav from './BottomNav';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiDelete, apiGet, apiPost } from '../utils/apiClient';
import { exportDataSet } from '../utils/exportData';
import { InstallAppRow } from './InstallAppRow';
import { useModalFocus } from '../hooks/useModalFocus';
import { PLAN_PRICES, TRIAL_DAYS, formatAudPrice } from '../utils/planPricing';

export function More({ onNavigate, onNavigateToFamily }) {
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [household, setHousehold] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasPremiumClaim, setHasPremiumClaim] = useState(false);
  const [accountProfile, setAccountProfile] = useState(null);
  const [accountProfileStatus, setAccountProfileStatus] = useState('loading'); // loading | ready | error
  const [leaveFlowStep, setLeaveFlowStep] = useState('');
  const [leaveFlowPending, setLeaveFlowPending] = useState('');
  const [leavePlanChoice, setLeavePlanChoice] = useState('family');
  // Inline messages (no toasts): page-level problems (sharing, sign-out), the leave flow, and Export.
  const [pageError, setPageError] = useState('');
  const [leaveError, setLeaveError] = useState('');
  const [exportStatus, setExportStatus] = useState(null); // { type: 'success' | 'error', text }
  const { signOut, user: authUser } = useAuth();

  const formatSubscriptionLabel = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'trial':
        return 'Free trial';
      case 'solo':
        return 'Solo';
      case 'family':
        return 'Family';
      // 'none' (hasn't chosen a plan) and 'free' (access we granted) are different states and
      // must not both read as "Free" — that was the whole point of separating them.
      case 'none':
        return 'No plan';
      case 'free':
        return 'Free';
      case 'expired':
        return 'Trial expired';
      // Unknown or not loaded yet: no label. Never guess "Free" — it flashed up on every load.
      default:
        return '';
    }
  };

  const formatPaymentStatusLabel = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'trialing':
        return 'Active';
      case 'active':
        return 'Active';
      case 'past_due':
        return 'Past due';
      case 'canceled':
      case 'cancelled':
        return 'Canceled';
      case 'expired':
        return 'Expired';
      default:
        return '';
    }
  };

  const formatDisplayDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const subscriptionTier = (accountProfile?.subscriptionTier || '').toLowerCase();
  const paymentStatus = (accountProfile?.paymentStatus || '').toLowerCase();
  const householdMembers = Array.isArray(household?.members) ? household.members : [];
  const isHouseholdMember = !!(household?.ownerUid && authUser?.uid && household.ownerUid !== authUser.uid);
  const householdOwner = householdMembers.find((member) => member?.uid === household?.ownerUid || member?.role === 'owner') || null;
  const ownerFirstName = (householdOwner?.name || '').trim().split(/\s+/)[0] || '';

  const user = {
    name: accountProfile?.displayName || authUser?.displayName || (authUser?.email ? authUser.email.split('@')[0] : 'Account'),
    email: accountProfile?.email || authUser?.email || 'Not set',
    subscriptionType: accountProfile?.subscriptionType || 'web',
    subscriptionPlan: formatSubscriptionLabel(subscriptionTier),
    subscriptionTier,
    paymentStatus,
    subscriptionStartDate: accountProfile?.subscriptionStartDate || null,
    subscriptionRenewalDate: accountProfile?.subscriptionRenewalDate || null,
    subscriptionEndDate: accountProfile?.subscriptionEndDate || null,
    trialEndDate: accountProfile?.trialEndDate || null,
    // During a trial: the plan they've already subscribed to, which starts when the trial ends.
    chosenPlan: accountProfile?.subscriptionPlan || null,
    subscriptionCancelled: accountProfile?.subscriptionCancelled === true,
    householdMemberCount: household?.members?.length || 1,
  };
  const householdRowTitle = isHouseholdMember ? 'Household' : 'Household Members';
  const householdRowSubtitle = householdLoading
    ? 'Loading members…'
    : isHouseholdMember
      ? `Member of ${ownerFirstName ? `${ownerFirstName}'s` : 'your'} household`
      : `${user.householdMemberCount} ${user.householdMemberCount === 1 ? 'member' : 'members'} • Manage invites`;
  const subscriptionLabel = isHouseholdMember
    ? `Included in ${ownerFirstName ? `${ownerFirstName}'s` : "the household owner's"} Family plan`
    : user.subscriptionPlan;
  const ownerHouseholdLabel = ownerFirstName ? `${ownerFirstName}'s household` : 'Your household';
  const hasHouseholdAccess = !!(
    hasPremiumClaim
    || authUser?.claims?.premium
    || authUser?.isPremium
    || authUser?.subscription === 'premium'
    || user.subscriptionTier === 'trial'
    || user.subscriptionTier === 'family'
  );
  // Family & Sharing is only shown to people who can actually use sharing: Family and trial
  // accounts, anyone given the premium flag, and household members (it's how they see and leave
  // their household). Solo and others upgrade from Plan & billing instead (decided 2026-10-11),
  // reached from the Plan & billing row below.
  const showFamilySection = isHouseholdMember || hasHouseholdAccess;

  // Plan & billing row: "Solo · Active" (status dropped when it only repeats the plan, e.g.
  // "Trial expired"), and a chip naming what the screen will let them do. The chip is a visual
  // label inside the row's single button, not a control of its own.
  const planStatus = formatPaymentStatusLabel(user.paymentStatus);
  const planSummary = accountProfileStatus === 'loading'
    ? 'Loading plan…'
    : accountProfileStatus === 'error'
      ? "Couldn't load your plan details"
      : planStatus && subscriptionTier !== 'expired'
        ? `${subscriptionLabel} · ${planStatus}`
        : subscriptionLabel;
  const showPlanChip = accountProfileStatus === 'ready';
  const planChip = subscriptionTier === 'expired'
    ? { label: 'Subscribe', strong: true }
    : subscriptionTier === 'trial' || subscriptionTier === 'none'
      ? { label: 'Choose plan', strong: false }
      : { label: 'Manage', strong: false };

  // Load premium claim from ID token so navigation works after granting premium.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authUser?.getIdTokenResult) return;
      try {
        const tokenResult = await authUser.getIdTokenResult();
        const claims = tokenResult?.claims || {};
        if (!cancelled && (claims.premium || claims.premiumUser)) {
          setHasPremiumClaim(true);
        }
      } catch (err) {
        console.warn('Failed to read premium claim:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authUser]);

  // Load household info for member count
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHouseholdLoading(true);
      try {
        const data = await apiGet('/households/me');
        if (!cancelled) setHousehold(data);
      } catch (err) {
        console.warn('Failed to load household:', err?.message || err);
      } finally {
        if (!cancelled) setHouseholdLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet('/user/me');
        if (!cancelled) {
          setAccountProfile(data);
          setAccountProfileStatus('ready');
        }
      } catch (err) {
        console.warn('Failed to load account profile:', err?.message || err);
        if (!cancelled) setAccountProfileStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleReferFriend = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Try this Meal Planner App',
        text: 'I\'ve been using this amazing meal planner app and thought you might like it!',
        url: 'https://yourmealplanner.com/invite',
      }).catch((error) => {
        // User cancelled - just ignore
        if (error.name === 'AbortError') {
          return;
        }
        // NotAllowedError or other errors - show helpful message
        if (error.name === 'NotAllowedError') {
          setPageError("Sharing isn't available here. Please try again from your phone's browser.");
        } else {
          console.error('Error sharing:', error);
          setPageError("We couldn't open sharing just now. Please try again.");
        }
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      setPageError("This browser doesn't support sharing. Try again from your phone.");
    }
  };

  const handleHelp = () => {
    window.open('https://www.mealplangenie.app/help', '_blank');
  };

  const handleOpenPlans = () => {
    navigate('/plans');
  };

  const closeLeaveFlow = () => {
    if (leaveFlowPending) return;
    setLeaveFlowStep('');
  };

  const handleHouseholdRowClick = () => {
    if (isHouseholdMember) {
      setLeaveError('');
      setLeaveFlowStep('details');
      return;
    }

    if (typeof onNavigateToFamily === 'function') {
      onNavigateToFamily();
    } else {
      navigate('/family');
    }
  };

  const handleLeaveHousehold = async () => {
    if (!authUser?.uid) return;

    try {
      setLeaveError('');
      setLeaveFlowPending('leaving');
      await apiDelete(`/households/members/${authUser.uid}`);
      setHousehold(null);
      // The success step itself confirms it ("You've left ..."), so no extra message.
      setLeaveFlowStep('success');
    } catch (error) {
      console.warn('Failed to leave household:', error);
      setLeaveError(error?.message || "We couldn't remove you from the household just now. Please try again.");
    } finally {
      setLeaveFlowPending('');
    }
  };

  const handleLeaveStartTrial = () => {
    navigate('/household-access/start-trial');
  };

  const handleLeaveNotNow = async () => {
    try {
      setLeaveFlowPending('later');
      await apiPost('/household-access/not-now', {});
      await signOut();
      navigate('/auth/signin', { replace: true });
    } catch (error) {
      console.warn('Failed to finish leave flow:', error);
      setLeaveError(error?.message || "We couldn't finish that just now. Please try again.");
    } finally {
      setLeaveFlowPending('');
    }
  };

  const handleExport = async (dataSetKey, format) => {
    try {
      setExportStatus(null);
      setIsExporting(true);
      await exportDataSet(dataSetKey, format);
      setExportStatus({ type: 'success', text: 'Your export has started downloading.' });
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus({ type: 'error', text: error?.message || "We couldn't export your data just now. Please try again." });
    } finally {
      setIsExporting(false);
    }
  };

  const navigate = useNavigate();
  // Bottom nav state and navigation
  const activeTab = 'more';
  const handleTabChange = (tab) => {
    if (typeof onNavigate === 'function') return onNavigate(tab);
    switch (tab) {
      case 'meal-plan':
        navigate('/MealPlan');
        break;
      case 'shopping-list':
        navigate('/ShoppingList');
        break;
      case 'recipes':
        navigate('/AllRecipes');
        break;
      case 'more':
        navigate('/More');
        break;
      default:
        break;
    }
  };

    // Responsive screen detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e) => {
      setIsLargeScreen(e.matches);
    };
    
    // Set initial value
    setIsLargeScreen(mediaQuery.matches);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);
    
    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth/signin', { replace: true });
    } catch (error) {
      console.error('Sign out failed', error);
      setPageError("We couldn't sign you out just now. Please try again.");
    }
  };


  // Keyboard behaviour for the two drawers rendered here (shared with the other More drawers):
  // focus to the first interactive element, Tab trapped, Escape closes, focus back to the row.
  const exportPanelRef = useRef(null);
  const exportTitleId = useId();
  useModalFocus({ isOpen: isExportOpen, panelRef: exportPanelRef, onClose: () => setIsExportOpen(false), canClose: !isExporting });
  const leavePanelRef = useRef(null);
  const leaveTitleId = useId();
  useModalFocus({ isOpen: leaveFlowStep !== '', panelRef: leavePanelRef, onClose: closeLeaveFlow, stepKey: leaveFlowStep, canClose: !leaveFlowPending });
  return (
     // mpg-focus-scope: the shared keyboard focus ring for everything on More, drawers included (globals.css).
     <div className="mpg-focus-scope min-h-screen bg-background flex justify-center text-foreground">
      <div 
        className="w-full bg-background min-h-screen relative pb-24"
        style={{ 
          maxWidth: isLargeScreen ? '1200px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >
        {/* Header */}
        <div className="h-8" />        
            <header className="py-3"                   
                style={{ 
                paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
                paddingRight: isLargeScreen ? '3rem' : '1.5rem'
              }}
          >
            <div className="flex items-center justify-between">
              <div className="w-10"></div>
            <h1 
              style={{ 
                fontSize: isLargeScreen ? '40px' : '20px',
                transition: 'font-size 0.3s ease'
              }}
            >
              More
            </h1>
          </div>
        </header>

        <div 
          className="mx-auto pt-4 space-y-6"
          style={{ 
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem'
          }}
        >

        </div>
        {/* Page-level problems (sharing, sign-out). Always mounted so new ones are announced. */}
        <div role="alert" className="px-2">
          {pageError && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#D4183D] bg-[#FEF3F2] py-1 pl-3 pr-1 text-sm text-[#B42318]">
              <p className="flex-1 py-2">{pageError}</p>
              <button type="button" onClick={() => setPageError('')} aria-label="Dismiss message" className="w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center hover:bg-[#FDE2E0]">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Profile Section */}
        <section>
          <h2 className="text-sm text-muted-foreground mb-3 px-2 mt-2">PROFILE</h2>
          <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => setIsEditProfileOpen(true)}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-foreground">{user.name}</div>
                <div className="text-sm text-muted-foreground">{user.email}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </section>

        {!isHouseholdMember && (
          <section>
            <h2 className="text-sm text-muted-foreground mb-3 px-2 mt-5">MEAL PLANNING</h2>
            <div className="bg-card rounded-2xl shadow-sm overflow-hidden divide-y divide-[var(--border)]">
              <button
                onClick={() => setIsPreferencesOpen(true)}
                className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-foreground">Preferences</div>
                  <div className="text-sm text-muted-foreground">Week start, meals & servings</div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </section>
        )}

        {/* Install app (PWA) — hides itself when already installed or not installable */}
        <InstallAppRow />

        {/* Family & Sharing Section */}
        {showFamilySection && (
        <section>
          <h2 className="text-sm text-muted-foreground mb-3 px-2 mt-5">FAMILY & SHARING</h2>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={handleHouseholdRowClick}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-[#E8F0ED] flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#6B8A78]" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-gray-900">{householdRowTitle}</div>
                <div className="text-sm text-muted-foreground">{householdRowSubtitle}</div>
              </div>
              {isHouseholdMember && (
                <div className="px-3 py-1 rounded-full bg-[#F0F3EC] text-[#6B8A78] text-xs font-medium">
                  Manage
                </div>
              )}
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </section>
        )}

        {/* Account Settings */}
        <section>
          <h2 className="text-sm text-muted-foreground mb-3 px-2 mt-5">ACCOUNT</h2>
          <div className="bg-card rounded-2xl shadow-sm overflow-hidden divide-y divide-[var(--border)]">
            {/* Plan & billing */}
            {isHouseholdMember ? (
              <div className="px-4 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-foreground">Plan &amp; billing</div>
                  <div className="text-sm text-muted-foreground">{subscriptionLabel}</div>
                </div>
              </div>
            ) : user.subscriptionType === 'apple' ? (
              <div className="px-4 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <Apple className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-foreground">Plan &amp; billing</div>
                  <div className="text-sm text-muted-foreground">{user.subscriptionPlan} via Apple</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Manage via your Apple account settings
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleOpenPlans}
                className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-foreground">Plan &amp; billing</div>
                  <div className="text-sm text-muted-foreground">{planSummary}</div>
                  <div className="mt-1 space-y-1 text-xs text-muted-foreground">
                    {/* Family is the top plan, so "upgrade" doesn't apply there (decided 2026-10-11). */}
                    {subscriptionTier !== 'family' && <div>Change plan, upgrade or manage billing</div>}
                    {user.subscriptionStartDate && (
                      <div>Started: {formatDisplayDate(user.subscriptionStartDate)}</div>
                    )}
                    {/* A cancelled plan runs to its end date and does not renew. */}
                    {user.subscriptionRenewalDate && !user.subscriptionEndDate && (
                      <div>Renews: {formatDisplayDate(user.subscriptionRenewalDate)}</div>
                    )}
                    {user.subscriptionEndDate && (
                      <div>
                        {user.subscriptionTier === 'trial'
                          ? (user.chosenPlan && !user.subscriptionCancelled
                            ? `${formatSubscriptionLabel(user.chosenPlan)} plan starts`
                            : 'Ends')
                          : 'Access until'}: {formatDisplayDate(user.subscriptionEndDate)}
                      </div>
                    )}
                  </div>
                </div>
                {showPlanChip && (
                <span
                  className={`inline-flex flex-shrink-0 items-center rounded-full px-3 py-1.5 text-xs ${
                    planChip.strong
                      ? 'bg-[#AFBB8B] font-semibold text-[#1F2814]'
                      : 'bg-[#F0F3EC] font-medium text-[#5F6B3F]'
                  }`}
                >
                  {planChip.label}
                </span>
                )}
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}

            {/* Change Password */}
            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-foreground">Change Password</div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {!isHouseholdMember && (
              <button
                onClick={() => { setExportStatus(null); setIsExportOpen(true); }}
                className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-foreground">Export your data</div>
                  <div className="text-sm text-muted-foreground">Meal plans, shopping list, recipes</div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <LogOut className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-foreground">Sign Out</div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Delete Account */}
            <button
              onClick={() => setIsDeleteAccountOpen(true)}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-[#F9EDEB] flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-[#9A4D41]" aria-hidden="true" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-[#9A4D41]">Delete Account</div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

          </div>
        </section>

        {/* Support & Other */}
        <section>
          <h2 className="text-sm text-muted-foreground mb-3 px-2 mt-5">SUPPORT</h2>
          <div className="bg-card rounded-2xl shadow-sm overflow-hidden divide-y divide-[var(--border)]">
            {/* Help */}
            <button
              onClick={handleHelp}
              className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-foreground">Help & Support</div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Refer a Friend */}
            {!isHouseholdMember && (
              <button
                onClick={handleReferFriend}
                className="w-full px-4 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#F9EDEB] flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-[#9A4D41]" aria-hidden="true" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-foreground">Refer a Friend</div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        </section>

        {/* App Version */}
        <div className="text-center text-sm text-muted-foreground pt-4 pb-6">
          Version 1.0.0
        </div>
      </div>

      {/* Drawers */}
      <EditProfileDrawer
        isOpen={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
        user={user}
        // The saved name only, not user.name (which falls back to the email prefix for display).
        currentName={accountProfile?.displayName || authUser?.displayName || ''}
        onSaved={(profile) => {
          setAccountProfile(profile);
          setAccountProfileStatus('ready');
        }}
      />
      <PreferencesDrawer
        isOpen={isPreferencesOpen}
        onClose={() => setIsPreferencesOpen(false)}
      />
      <ChangePasswordDrawer
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
      <DeleteAccountDrawer
        isOpen={isDeleteAccountOpen}
        onClose={() => setIsDeleteAccountOpen(false)}
      />

      {isExportOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            aria-hidden="true"
            onClick={() => {
              if (!isExporting) {
                setIsExportOpen(false);
              }
            }}
          />
          <div
            ref={exportPanelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={exportTitleId}
            tabIndex={-1}
            className="fixed inset-x-4 bottom-10 z-50 bg-white rounded-3xl shadow-2xl px-6 py-6 max-w-[560px] mx-auto focus:outline-none"
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-sm text-[#757575]">Export</p>
                <h3 id={exportTitleId} className="text-xl text-gray-900">Take your data with you</h3>
                <p className="text-sm text-[#757575] mt-2">
                  Download your full meal plan history, full shopping list history, or recipes in CSV or text format.
                </p>
              </div>
<button
                type="button"
                onClick={() => {
                  if (!isExporting) {
                    setIsExportOpen(false);
                  }
                }}
                aria-label="Close"
                className="w-11 h-11 -mr-2 -mt-2 flex-shrink-0 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
              </button>
            </div>

            <div role="status" aria-live="polite">
              {exportStatus?.type === 'success' && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>{exportStatus.text}</span>
                </div>
              )}
            </div>
            <div role="alert">
              {exportStatus?.type === 'error' && (
                <p className="mb-4 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-3 py-2 text-sm text-[#B42318]">{exportStatus.text}</p>
              )}
            </div>

            <div className="space-y-3">
              {[
                { key: 'mealPlans', title: 'Meal plans', description: 'Every saved week in your planner across all populated dates' },
                { key: 'shoppingList', title: 'Shopping list', description: 'All generated and manual shopping items across every saved week' },
                { key: 'recipes', title: 'Recipes', description: 'All imported and created recipes' },
              ].map((item) => (
                <div key={item.key} className="rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F0F3EC] flex items-center justify-center flex-shrink-0">
                      {item.key === 'recipes' ? (
                        <FileText className="w-5 h-5 text-[#6B8A78]" />
                      ) : (
                        <Table2 className="w-5 h-5 text-[#6B8A78]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900">{item.title}</div>
                      <div className="text-sm text-[#757575] mt-1">{item.description}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      type="button"
                      disabled={isExporting}
                      onClick={() => handleExport(item.key, 'csv')}
                      className="flex-1 rounded-full bg-[#F0F3EC] px-4 py-3 text-sm font-medium text-[#5F6B3F] hover:bg-[#E4EAD9] transition-colors disabled:opacity-60"
                    >
                      Export CSV
                    </button>
                    <button
                      type="button"
                      disabled={isExporting}
                      onClick={() => handleExport(item.key, 'txt')}
                      className="flex-1 rounded-full bg-[#AFBB8B] px-4 py-3 text-sm font-medium text-[#1F2814] hover:bg-[#9AA876] transition-colors disabled:opacity-60"
                    >
                      Export text
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {isExporting && (
              <p className="mt-4 text-sm text-[#757575]">Preparing your export…</p>
            )}
          </div>
        </>
      )}

      {leaveFlowStep !== '' && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            aria-hidden="true"
            onClick={closeLeaveFlow}
          />

          {leaveFlowStep === 'details' && (
            <div ref={leavePanelRef} role="dialog" aria-modal="true" aria-labelledby={leaveTitleId} tabIndex={-1} className="fixed inset-x-4 bottom-10 z-50 bg-white rounded-3xl shadow-2xl px-6 py-6 max-w-[448px] mx-auto focus:outline-none">
              <div className="flex justify-end -mb-6">
                <button
                  type="button"
                  onClick={closeLeaveFlow}
                  aria-label="Close"
                  className="w-11 h-11 -mr-2 -mt-2 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
                </button>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#F0F3EC] flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-[#6B8A78]" />
              </div>
              <h3 id={leaveTitleId} className="text-xl text-gray-900 text-center">{ownerHouseholdLabel}</h3>
              <p className="mt-3 text-sm leading-6 text-[#757575] text-center">
                You're a member of {ownerHouseholdLabel}. You have access to the shared meal plan, recipes, and shopping list.
              </p>

              <div className="mt-6 overflow-hidden rounded-2xl border border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setLeaveFlowStep('confirm')}
                  className="w-full px-4 py-4 text-left text-[#A32D2D] hover:bg-[#FFF7F7] transition-colors"
                >
                  Leave this household
                </button>
              </div>
            </div>
          )}

          {leaveFlowStep === 'confirm' && (
            <div ref={leavePanelRef} role="dialog" aria-modal="true" aria-labelledby={leaveTitleId} tabIndex={-1} className="fixed inset-x-4 bottom-10 z-50 bg-white rounded-3xl shadow-2xl px-6 py-6 max-w-[448px] mx-auto focus:outline-none">
              <div className="w-12 h-12 rounded-full bg-[#FCEBEB] flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-[#A32D2D]" />
              </div>
              <h3 id={leaveTitleId} className="text-xl text-gray-900 text-center">Leave {ownerHouseholdLabel}?</h3>
              <p className="mt-3 text-sm leading-6 text-[#757575] text-center">
                You'll lose access to the shared meal plan, recipes, and shopping list. This can't be undone. {ownerFirstName || 'The owner'} would need to re-invite you.
              </p>

              <div role="alert">
                {leaveError && (
                  <p className="mt-4 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-3 py-2 text-sm text-[#B42318]">{leaveError}</p>
                )}
              </div>
              <div className="mt-6 overflow-hidden rounded-2xl border border-[var(--border)]">
                <button
                  type="button"
                  disabled={leaveFlowPending !== ''}
                  onClick={handleLeaveHousehold}
                  className="w-full px-4 py-4 text-left text-[#A32D2D] hover:bg-[#FFF7F7] transition-colors disabled:opacity-60"
                >
                  Yes, leave household
                </button>
                <button
                  type="button"
                  disabled={leaveFlowPending !== ''}
                  onClick={() => setLeaveFlowStep('details')}
                  className="w-full px-4 py-4 text-left border-t border-[var(--border)] hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {leaveFlowStep === 'success' && (
            <div ref={leavePanelRef} role="dialog" aria-modal="true" aria-labelledby={leaveTitleId} tabIndex={-1} className="fixed inset-x-4 bottom-6 z-50 bg-white rounded-3xl shadow-2xl px-6 py-6 max-w-[448px] mx-auto focus:outline-none">
              <div className="w-14 h-14 rounded-full bg-[#E8F0ED] flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-[#4F7A5A]" />
              </div>
              <h3 id={leaveTitleId} className="text-[28px] leading-8 text-gray-900 text-center">You've left {ownerHouseholdLabel}</h3>
              <p className="mt-3 text-sm leading-6 text-[#757575] text-center">
                We're sorry to see you go. You can keep meal planning with your own account. Start with a free trial, no credit card needed.
              </p>
              <div role="alert">
                {leaveError && (
                  <p className="mt-4 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-3 py-2 text-sm text-[#B42318]">{leaveError}</p>
                )}
              </div>

              <div className="mt-5 rounded-2xl bg-[#E8F0ED] px-4 py-4">
                <div className="flex items-center gap-2 text-[#27500A]">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-base font-medium">{TRIAL_DAYS} days free</span>
                </div>
                <p className="mt-2 text-sm leading-6 text-[#3B6D11]">
                  Try Solo or Family on your own terms before any billing begins.
                </p>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setLeavePlanChoice('solo')}
                  className={`rounded-2xl border px-4 py-4 text-left transition-colors ${leavePlanChoice === 'solo' ? 'border-[#4A7C59] bg-[#F7FBF7]' : 'border-[var(--border)] bg-white hover:bg-gray-50'}`}
                >
                  <div className="text-base text-gray-900">Solo</div>
                  <div className="mt-1 text-sm text-[#5F6757]">{formatAudPrice(PLAN_PRICES.solo.monthly)}/month</div>
                  <div className="mt-1 text-sm text-muted-foreground">Just for you</div>
                </button>
                <button
                  type="button"
                  onClick={() => setLeavePlanChoice('family')}
                  className={`rounded-2xl border px-4 py-4 text-left transition-colors ${leavePlanChoice === 'family' ? 'border-[#4A7C59] bg-[#F7FBF7]' : 'border-[var(--border)] bg-white hover:bg-gray-50'}`}
                >
                  <div className="text-xs font-medium text-[#4A7C59]">Popular</div>
                  <div className="mt-1 text-base text-gray-900">Family</div>
                  <div className="mt-1 text-sm text-[#5F6757]">{formatAudPrice(PLAN_PRICES.family.monthly)}/month</div>
                  <div className="mt-1 text-sm text-muted-foreground">Up to 4 members</div>
                </button>
              </div>

              <button
                type="button"
                disabled={leaveFlowPending !== ''}
                onClick={handleLeaveStartTrial}
                className="mt-5 w-full rounded-2xl bg-[#AFBB8B] px-4 py-4 text-base font-medium text-[#1F2814] hover:bg-[#9FAA7B] transition-colors disabled:opacity-60"
              >
                Start my free trial
              </button>

              <button
                type="button"
                disabled={leaveFlowPending !== ''}
                onClick={handleLeaveNotNow}
                className="mt-3 w-full rounded-2xl border border-[var(--border)] bg-transparent px-4 py-4 text-base text-gray-900 hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Not now
              </button>

              <p className="mt-4 text-xs leading-5 text-[#757575] text-center">
                We'll send you a confirmation email. You can always sign in and start a plan later.
              </p>
            </div>
          )}
        </>
      )}

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

export default More;
